import axios from 'axios';
import { normalize } from 'normalizr';
import querystring from 'querystring';
import AuthUtils from '../utils/AuthUtils';
import * as ActionTypes from '../actions/types';

// API settings
const API_ROOT = window.env && window.env.API_URL ? window.env.API_URL : 'http://localhost:8080';
const COMMON_HEADERS = {
  Accept: 'application/json',
};

// OAuth 2.0 server settings
const TOKEN_URL = window.env && window.env.TOKEN_URL ? window.env.TOKEN_URL : 'http://localhost:8080/oauth/token';
const CLIENT_ID = 'clientId';
const AUTH_HEADERS = {
  Accept: 'application/json',
  Authorization: `Basic ${btoa(`${CLIENT_ID}:`)}`,
};

// Utility functions
const actionWith = (action, data, middlewareAction) => {
  const finalAction = { ...action, ...data };
  delete finalAction[middlewareAction];
  return finalAction;
};

const validateTypes = (types) => {
  if (!Array.isArray(types) || types.length !== 3) {
    throw new Error('Expected an array of three action types.');
  }
  if (!types.every((type) => typeof type === 'string')) {
    throw new Error('Expected action types to be strings.');
  }
};

const prepareHeaders = () => ({ ...COMMON_HEADERS, Authorization: `Bearer ${AuthUtils.getAccessToken()}` });

const validateCredentials = (username, password) => {
  if (typeof username !== 'string') {
    throw new Error('Specify a string username.');
  }
  if (typeof password !== 'string') {
    throw new Error('Specify a string password.');
  }
};

const authenticate = ({ username, password, rememberMe }) => {
  validateCredentials(username, password);

  const body = querystring.stringify({
    username,
    password,
    grant_type: 'password',
  });

  return axios.post(TOKEN_URL, body, { headers: AUTH_HEADERS })
    .then((response) => {
      AuthUtils.saveToken(response.data, rememberMe);
    });
};

const refreshAccessToken = (store) => {
  const refreshToken = AuthUtils.getRefreshToken();
  if (refreshToken) {
    const body = querystring.stringify({
      refresh_token: refreshToken,
      grant_type: 'refresh_token',
    });

    return axios.post(TOKEN_URL, body, { headers: AUTH_HEADERS }).then((response) => {
      AuthUtils.saveToken(response.data, true);
    }).catch(() => {
      AuthUtils.destroyToken();
    });
  }

  const error = new Error('Session expired.');
  error.expiredSession = true;

  store.dispatch({ type: ActionTypes.UPDATE_ERROR, message: error.message });
  store.dispatch({ type: ActionTypes.LOGOUT_SUCCESS });

  return Promise.reject(error);
};

const validateEndPoint = (endpoint) => {
  if (typeof endpoint !== 'string') {
    throw new Error('Specify a string endpoint URL.');
  }
};

const executeCall = ({
  endpoint, method, schema, body,
}) => {
  validateEndPoint(endpoint);
  const fullUrl = (endpoint.indexOf(API_ROOT) === -1) ? API_ROOT + endpoint : endpoint;
  const headers = prepareHeaders();

  return axios({
    url: fullUrl, method, headers, data: body,
  })
    .then((response) => {
      if (schema) {
        if (response.data) {
          if (response.data.content) {
            return {
              ...normalize(response.data.content, schema),
              totalPages: response.data.totalPages,
            };
          }
          return { ...normalize(response.data, schema) };
        }
      }
      return response.data;
    });
};

const handleError = (error, next, failureType, action, executeType) => {
  const apiError = error.response ? error.response.data : null;
  let message = executeType === ActionTypes.AUTHENTICATE ? 'Authentication failed' : error.message || 'Something bad happened';
  const details = [];
  if (apiError) {
    if (apiError.error_description) {
      message = apiError.error_description;
    } else if (apiError.message) {
      message = apiError.message;
      if (apiError.validationErrors) {
        apiError.validationErrors.forEach((validationError) => {
          details.push(`${validationError.field}: ${validationError.message}`);
        });
      }
    }
  }

  next(actionWith(action, { type: failureType }, executeType));

  if (!error.expiredSession) {
    const newError = new Error(message);
    newError.details = details;
    throw newError;
  }
};

export default (store) => (next) => (action) => {
  let targetFunction;
  let executeType;

  if (action[ActionTypes.API_CALL]) {
    executeType = ActionTypes.API_CALL;
    targetFunction = executeCall;
  } else if (action[ActionTypes.AUTHENTICATE]) {
    executeType = ActionTypes.AUTHENTICATE;
    targetFunction = authenticate;
  }

  if (typeof executeType === 'undefined') {
    return next(action);
  }

  store.dispatch({ type: ActionTypes.RESET_ERROR });

  const { types, ...config } = action[executeType];
  validateTypes(types);

  const [requestType, successType, failureType] = types;
  next(actionWith(action, { type: requestType }, executeType));

  let promise;
  if (executeType !== ActionTypes.AUTHENTICATE && !AuthUtils.getAccessToken()) {
    promise = refreshAccessToken(store).then(() => targetFunction(config));
  } else {
    promise = targetFunction(config);
  }

  return promise.then((response) => next(
    actionWith(action, { response, type: successType }, executeType),
  ),
  (error) => handleError(error, next, failureType));
};
